import { Container, Graphics } from 'pixi.js';
import { BALL_SPEED } from '../constants';

export class Ball {
  public container: Container;
  public x: number;
  public y: number;
  public vx: number = 0;
  public vy: number = 0;
  public readonly radius: number = 14;

  public state: 'idle' | 'chased' | 'caught' = 'idle';
  public chaseTimer: number = 0;
  public gracePeriod: number = 0;

  private headingAngle: number = 0;
  private currentSpeed: number = BALL_SPEED;
  private wanderTimer: number = 0;
  private animTimer: number = 0;

  private visualGfx: Graphics;
  private glowGfx: Graphics;
  private eyesGfx: Graphics;

  constructor(x: number, y: number) {
    this.container = new Container();
    this.x = x;
    this.y = y;
    this.container.position.set(x, y);

    this.glowGfx = new Graphics();
    this.visualGfx = new Graphics();
    this.eyesGfx = new Graphics();

    this.container.addChild(this.glowGfx);
    this.container.addChild(this.visualGfx);
    this.container.addChild(this.eyesGfx);

    this.drawBall();
  }

  private drawBall() {
    this.visualGfx.clear();
    // Vibrant magenta-purple lively yarn ball
    this.visualGfx.circle(0, 0, this.radius);
    this.visualGfx.fill({ color: 0xff007f });
    this.visualGfx.stroke({ width: 2, color: 0x880044 });

    // Cute yarn windings
    this.visualGfx.arc(0, 0, 8, -0.6, 2.4);
    this.visualGfx.stroke({ width: 1.5, color: 0xff77aa });
    this.visualGfx.arc(0, 0, 5, 1.0, 4.0);
    this.visualGfx.stroke({ width: 1.5, color: 0xffaacc });

    // Playful thread tail
    this.visualGfx.moveTo(-5, 7);
    this.visualGfx.quadraticCurveTo(-12, 14, -16, 10);
    this.visualGfx.stroke({ width: 2.2, color: 0xff007f, cap: 'round' });
  }

  private drawEyes(isScared: boolean, lookAngle: number) {
    this.eyesGfx.clear();

    const eyeOffsetX = Math.cos(lookAngle) * 3;
    const eyeOffsetY = Math.sin(lookAngle) * 3;

    if (isScared) {
      this.eyesGfx.circle(-5 + eyeOffsetX, -4 + eyeOffsetY, 4);
      this.eyesGfx.fill({ color: 0xffffff });
      this.eyesGfx.circle(-5 + eyeOffsetX, -4 + eyeOffsetY, 2);
      this.eyesGfx.fill({ color: 0x000000 });

      this.eyesGfx.circle(5 + eyeOffsetX, -4 + eyeOffsetY, 4);
      this.eyesGfx.fill({ color: 0xffffff });
      this.eyesGfx.circle(5 + eyeOffsetX, -4 + eyeOffsetY, 2);
      this.eyesGfx.fill({ color: 0x000000 });
    } else {
      this.eyesGfx.moveTo(-7, -2);
      this.eyesGfx.lineTo(-3, -2);
      this.eyesGfx.moveTo(3, -2);
      this.eyesGfx.lineTo(7, -2);
      this.eyesGfx.stroke({ width: 2, color: 0x2c1810 });
    }
  }

  public triggerChase(catX: number, catY: number, timeLimit: number) {
    this.state = 'chased';
    this.chaseTimer = timeLimit;
    this.gracePeriod = 0.5;

    this.headingAngle = Math.atan2(this.y - catY, this.x - catX);
    this.currentSpeed = BALL_SPEED * 1.1;
  }

  public canBeCaught(): boolean {
    return this.state === 'chased' && this.gracePeriod <= 0;
  }

  public update(delta: number, catX: number, catY: number) {
    this.animTimer += delta * 0.1;

    if (this.state === 'idle') {
      this.glowGfx.clear();
      this.glowGfx.circle(0, 0, this.radius + 6 + Math.sin(this.animTimer * 2) * 2.5);
      this.glowGfx.fill({ color: 0xff007f, alpha: 0.35 });

      this.visualGfx.scale.set(1.0 + Math.sin(this.animTimer) * 0.05, 1.0 - Math.sin(this.animTimer) * 0.05);
      this.drawEyes(false, 0);
    } else if (this.state === 'chased') {
      const deltaSec = delta / 60;
      this.chaseTimer -= deltaSec;
      if (this.gracePeriod > 0) {
        this.gracePeriod -= deltaSec;
      }

      this.wanderTimer += delta * 0.035;

      // 1. Organic Sentient Heading
      const dxToCat = this.x - catX;
      const dyToCat = this.y - catY;
      const distToCat = Math.sqrt(dxToCat * dxToCat + dyToCat * dyToCat);
      const fleeAngle = Math.atan2(dyToCat, dxToCat);

      const curvyWiggle = Math.sin(this.wanderTimer * 2.2) * 0.55 + Math.cos(this.wanderTimer * 1.1) * 0.25;
      let desiredAngle = fleeAngle + curvyWiggle;

      // 2. Predictive steering around walls
      const lookAheadDist = 60;
      const futureX = this.x + Math.cos(this.headingAngle) * lookAheadDist;
      const futureY = this.y + Math.sin(this.headingAngle) * lookAheadDist;

      // Outer boundaries soft avoidance
      if (futureX < 120) desiredAngle = Math.atan2(Math.sin(desiredAngle), Math.abs(Math.cos(desiredAngle)) + 0.8);
      if (futureX > 880) desiredAngle = Math.atan2(Math.sin(desiredAngle), -Math.abs(Math.cos(desiredAngle)) - 0.8);
      if (futureY < 120) desiredAngle = Math.atan2(Math.abs(Math.sin(desiredAngle)) + 0.8, Math.cos(desiredAngle));
      if (futureY > 580) desiredAngle = Math.atan2(-Math.abs(Math.sin(desiredAngle)) - 0.8, Math.cos(desiredAngle));

      // Middle vertical wall avoidance (x = 500)
      if (futureX > 460 && futureX < 540) {
        const inTopDoor = futureY >= 170 && futureY <= 240;
        const inBottomDoor = futureY >= 480 && futureY <= 550;
        if (!inTopDoor && !inBottomDoor) {
          const targetY = futureY < 350 ? 205 : 515;
          const doorAngle = Math.atan2(targetY - this.y, this.x < 500 ? -25 : 25);
          desiredAngle = desiredAngle * 0.3 + doorAngle * 0.7;
        }
      }

      // Horizontal walls avoidance (y = 360)
      if (futureY > 320 && futureY < 400) {
        const inDoor = (futureX >= 240 && futureX <= 320) || (futureX >= 700 && futureX <= 780);
        if (!inDoor) {
          const targetX = futureX < 500 ? 280 : 740;
          const doorAngle = Math.atan2(this.y < 360 ? -25 : 25, targetX - this.x);
          desiredAngle = desiredAngle * 0.3 + doorAngle * 0.7;
        }
      }

      // Smooth Angular Steering
      let angleDiff = desiredAngle - this.headingAngle;
      while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;
      while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;

      this.headingAngle += angleDiff * (0.12 * delta);

      const speedMod = distToCat < 130 ? 1.15 : 0.95;
      this.currentSpeed = BALL_SPEED * speedMod;

      this.vx = Math.cos(this.headingAngle) * this.currentSpeed;
      this.vy = Math.sin(this.headingAngle) * this.currentSpeed;

      let nextX = this.x + this.vx * delta;
      let nextY = this.y + this.vy * delta;

      // 3. Robust Depenetration (Анти-застревание: выталкивание наружу при соприкосновении)
      // Outer bounds
      const minX = 70;
      const maxX = 930;
      const minY = 70;
      const maxY = 630;

      if (nextX < minX) {
        nextX = minX;
        this.headingAngle = Math.atan2(Math.sin(this.headingAngle), Math.abs(Math.cos(this.headingAngle)));
      } else if (nextX > maxX) {
        nextX = maxX;
        this.headingAngle = Math.atan2(Math.sin(this.headingAngle), -Math.abs(Math.cos(this.headingAngle)));
      }

      if (nextY < minY) {
        nextY = minY;
        this.headingAngle = Math.atan2(Math.abs(Math.sin(this.headingAngle)), Math.cos(this.headingAngle));
      } else if (nextY > maxY) {
        nextY = maxY;
        this.headingAngle = Math.atan2(-Math.abs(Math.sin(this.headingAngle)), Math.cos(this.headingAngle));
      }

      // Middle vertical wall (x = 500)
      const wallX = 500;
      const wallHalf = 10;
      if (nextX + this.radius >= wallX - wallHalf && nextX - this.radius <= wallX + wallHalf) {
        const inTopDoor = nextY >= 170 && nextY <= 240;
        const inBottomDoor = nextY >= 480 && nextY <= 550;

        if (!inTopDoor && !inBottomDoor) {
          // Push out decisively based on which side the ball approached from
          if (this.x <= wallX) {
            nextX = wallX - wallHalf - this.radius - 1;
            this.headingAngle = Math.atan2(Math.sin(this.headingAngle), -Math.abs(Math.cos(this.headingAngle)));
          } else {
            nextX = wallX + wallHalf + this.radius + 1;
            this.headingAngle = Math.atan2(Math.sin(this.headingAngle), Math.abs(Math.cos(this.headingAngle)));
          }
        }
      }

      // Horizontal dividing walls (y = 360)
      const wallY = 360;
      if (nextY + this.radius >= wallY - wallHalf && nextY - this.radius <= wallY + wallHalf) {
        const inLeftDoor = nextX < 500 && nextX >= 240 && nextX <= 320;
        const inRightDoor = nextX >= 500 && nextX >= 700 && nextX <= 780;

        if (!inLeftDoor && !inRightDoor) {
          // Push out decisively
          if (this.y <= wallY) {
            nextY = wallY - wallHalf - this.radius - 1;
            this.headingAngle = Math.atan2(-Math.abs(Math.sin(this.headingAngle)), Math.cos(this.headingAngle));
          } else {
            nextY = wallY + wallHalf + this.radius + 1;
            this.headingAngle = Math.atan2(Math.abs(Math.sin(this.headingAngle)), Math.cos(this.headingAngle));
          }
        }
      }

      this.x = nextX;
      this.y = nextY;

      // 4. Lively Visuals
      this.glowGfx.clear();
      this.glowGfx.circle(0, 0, this.radius + 8);
      this.glowGfx.fill({ color: 0xffd32a, alpha: 0.5 });

      const scamper = Math.sin(this.animTimer * 4) * 0.1;
      this.visualGfx.scale.set(1.0 + scamper, 1.0 - scamper);
      this.visualGfx.rotation = this.headingAngle;

      this.drawEyes(true, fleeAngle - this.headingAngle);
      this.container.position.set(this.x, this.y);

      if (this.chaseTimer <= 0) {
        this.state = 'idle';
        this.vx = 0;
        this.vy = 0;
      }
    } else if (this.state === 'caught') {
      this.glowGfx.clear();
      this.drawEyes(false, 0);
      this.visualGfx.scale.set(0.9);
      this.container.alpha = 0.6;
    }
  }

  public catch() {
    this.state = 'caught';
    this.vx = 0;
    this.vy = 0;
  }

  public reset(x: number, y: number) {
    this.x = x;
    this.y = y;
    this.state = 'idle';
    this.vx = 0;
    this.vy = 0;
    this.chaseTimer = 0;
    this.gracePeriod = 0;
    this.headingAngle = 0;
    this.currentSpeed = BALL_SPEED;
    this.container.alpha = 1.0;
    this.container.position.set(x, y);
    this.drawBall();
  }
}
